import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Button, Dropdown, Flex } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { DSIcon } from "ui/Icon/DSIcon"
import CheckboxFilterPanel from "ui/Filters/CheckboxFilterPanel"
import { UNASSIGNED_ID } from "../../constants"
import styles from "./index.module.css"

const CREW_PREFIX = "crew-"
const EQUIPMENT_PREFIX = "equip-"

/**
 * The desktop "Experience" and "Resources" filters.
 *
 * Crew and equipment share one "Resources" dropdown, as the design shows, so both lists arrive
 * as labelled groups of the same panel the Bookings header uses. Their ids are only unique within
 * their own list, so each is prefixed on the way in and stripped on the way out.
 */
const DesktopFilterPills = ({
  filters,
  setFilters,
  handleFilterChange,
  experienceOptions,
  crewMemberOptionsWithUnassigned,
  equipmentOptionsWithUnassigned
}) => {
  const { t } = useTranslation()
  const [openPanel, setOpenPanel] = useState(null)

  const experienceGroups = useMemo(
    () => [{ key: "experiences", options: experienceOptions.map((option) => ({ id: option.value, label: option.label })) }],
    [experienceOptions]
  )

  const resourceGroups = useMemo(
    () => [
      {
        key: "crew",
        label: t("calendar.filters.crew_member", "Crew Member"),
        iconName: "users",
        options: crewMemberOptionsWithUnassigned.map((option) => ({ id: `${CREW_PREFIX}${option.value}`, label: option.label }))
      },
      {
        key: "equipment",
        label: t("calendar.filters.equipment", "Equipment"),
        iconName: "box",
        options: equipmentOptionsWithUnassigned.map((option) => ({ id: `${EQUIPMENT_PREFIX}${option.value}`, label: option.label }))
      }
    ],
    [crewMemberOptionsWithUnassigned, equipmentOptionsWithUnassigned, t]
  )

  const selectedResourceIds = useMemo(
    () => [
      ...(filters.crew || []).map((id) => `${CREW_PREFIX}${id}`),
      ...(filters.equipment || []).map((id) => `${EQUIPMENT_PREFIX}${id}`)
    ],
    [filters.crew, filters.equipment]
  )

  /** Both halves are written in one update: they come from the same saved-filters snapshot. */
  const applyResources = useCallback(
    (ids) => {
      const crew = ids.filter((id) => id.startsWith(CREW_PREFIX)).map((id) => id.slice(CREW_PREFIX.length))
      const equipment = ids.filter((id) => id.startsWith(EQUIPMENT_PREFIX)).map((id) => id.slice(EQUIPMENT_PREFIX.length))
      // "Unassigned" is the only non-numeric id, so everything else goes back to a number.
      const toId = (id) => (id === UNASSIGNED_ID || id === `${UNASSIGNED_ID}_equipment` ? id : Number(id))
      setFilters((prev) => ({ ...prev, crew: crew.map(toId), equipment: equipment.map(toId) }))
      setOpenPanel(null)
    },
    [setFilters]
  )

  const renderPill = ({ key, label, iconName, count, groups, selectedIds, onApply }) => (
    <Dropdown
      open={openPanel === key}
      onOpenChange={(isOpen) => setOpenPanel(isOpen ? key : null)}
      trigger={["click"]}
      dropdownRender={() => (
        <CheckboxFilterPanel
          title={t("filters.title", "Filter")}
          groups={groups}
          selectedIds={selectedIds}
          onApply={onApply}
          onClose={() => setOpenPanel(null)}
        />
      )}
    >
      <Button className={`${styles.pill} ${count || openPanel === key ? styles.pillActive : ""}`}>
        {iconName && <DSIcon name={iconName} width={20} height={20} />}
        <span className={styles.pillLabel}>{label}</span>
        {count > 0 && <span className={styles.pillCount}>{count}</span>}
        <DownOutlined />
      </Button>
    </Dropdown>
  )

  return (
    <Flex align="center" gap="var(--spacing-xs)" className={styles.pills}>
      {renderPill({
        key: "experience",
        label: t("calendar.filters.experience", "Experience"),
        count: filters.experience?.length || 0,
        groups: experienceGroups,
        selectedIds: filters.experience || [],
        onApply: (ids) => {
          handleFilterChange("experience")(ids)
          setOpenPanel(null)
        }
      })}
      {renderPill({
        key: "resources",
        label: t("calendar.filters.resources", "Resources"),
        iconName: "box",
        count: selectedResourceIds.length,
        groups: resourceGroups,
        selectedIds: selectedResourceIds,
        onApply: applyResources
      })}
    </Flex>
  )
}

DesktopFilterPills.propTypes = {
  filters: PropTypes.shape({
    experience: PropTypes.array,
    crew: PropTypes.array,
    equipment: PropTypes.array
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  experienceOptions: PropTypes.array.isRequired,
  crewMemberOptionsWithUnassigned: PropTypes.array.isRequired,
  equipmentOptionsWithUnassigned: PropTypes.array.isRequired
}

export default DesktopFilterPills
